import {
  type PathLookupEdgeType,
  pathLookupEdgeAttributes,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { sortPathLookupProtocols } from 'components/Graph/commands/pathLookup/sortPathLookupProtocols';
import { DnDIcon } from 'components/icons/DnDIcon';
import { type FC, memo, useRef } from 'react';
import { type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { canSort } from 'utils/dnd/canSort';
import { useEmptyPreview } from 'utils/dnd/useEmptyPreview';

export const DRAG_ITEM = 'pathLookupSetupProtocol';

export interface PathlookupProtocolDragItem {
  color: string;
  index: number;
  name: PathLookupEdgeType;
  type: string;
}

interface DraggableProtocolProps {
  color: string;
  draggedProtocol: PathLookupEdgeType | null;
  index: number;
  name: PathLookupEdgeType;
  onProtocolIsDraggedChange: (value: PathLookupEdgeType | null) => void;
}

const BarePathlookupDraggableProtocol: FC<DraggableProtocolProps> = (props) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
  });

  const protocolIsDragged = props.draggedProtocol === props.name;

  const [, drag, preview] = useDrag({
    item: {
      color: props.color,
      index: props.index,
      name: props.name,
      type: DRAG_ITEM,
    },
    begin: () => {
      setTimeout(() => {
        props.onProtocolIsDraggedChange(props.name);
      }, 0);
    },
    end: () => {
      props.onProtocolIsDraggedChange(null);
    },
  });

  const [, drop] = useDrop({
    accept: DRAG_ITEM,
    hover(item: PathlookupProtocolDragItem, monitor: DropTargetMonitor) {
      if (!itemRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      if (!canSort(itemRef.current, monitor, dragIndex, hoverIndex)) {
        return;
      }

      ctxProps.dispatch(sortPathLookupProtocols, {
        dragIndex,
        hoverIndex,
      });

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.

      // Example: https://github.com/react-dnd/react-dnd/blob/09838c9c203554bfe23c97f7af82b120f7f28be8/packages/documentation/examples-hooks/src/04-sortable/simple/Card.tsx
      item.index = hoverIndex;
    },
  });

  drag(drop(itemRef));

  useEmptyPreview(preview);

  return (
    <div
      className={classNames(
        props.draggedProtocol === null &&
          'pathlookup-draggable-protocol--nothing-dragged',
        'p-lgu-5 flex cursor-move',
        protocolIsDragged && 'graph-draggable-item__box-border',
      )}
      ref={itemRef}
    >
      <div
        className={classNames(
          'flex flex-1 items-center',
          protocolIsDragged && 'invisible',
        )}
      >
        <DnDIcon title='Drag to move' width={6} height={10} />
        <div
          className='w-lgu-6 h-lgu-6 ml-10 mr-2.5'
          style={{ background: props.color }}
        />
        <div className='flex-1'>
          {pathLookupEdgeAttributes[props.name].name}
        </div>
      </div>
    </div>
  );
};

export const PathlookupDraggableProtocol = memo(
  BarePathlookupDraggableProtocol,
);
